export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      'x-pma-request': '1',
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 204) return undefined as T;
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = payload?.error ?? {};
    if (response.status === 401 && !path.startsWith('/auth/')) window.dispatchEvent(new CustomEvent('pma:unauthenticated'));
    throw new ApiError(response.status, error.code ?? 'error', error.message ?? response.statusText, error.details);
  }
  return payload as T;
}

async function text(path: string): Promise<string> {
  const response = await fetch(`/api/v1${path}`, { credentials: 'same-origin', headers: { 'x-pma-request': '1' } });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new ApiError(response.status, payload?.error?.code ?? 'error', payload?.error?.message ?? response.statusText);
  }
  return response.text();
}

export const api = {
  text,
  get: <T = any>(path: string) => request<T>('GET', path),
  post: <T = any>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  patch: <T = any>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: (path: string, body?: unknown) => request<void>('DELETE', path, body),
};

export interface Me {
  user: { id: string; email: string; name: string; locale: string; notification_email: string };
  organizationId: string | null;
  role: 'owner' | 'admin' | 'member' | 'guest' | null;
  organizations: { id: string; name: string; plan: string; role: string }[];
}
